import React, { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { useAuth } from './context/AuthContext';
import { db, isSupabaseEnabled } from './lib/supabase';
import { STAGES, PLATFORMS, DEAL_TYPES, INDUSTRIES, FOLLOWER_RANGES, INVOICE_STATUS, CONTRACT_STATUS, APPLICATION_STATUS, CONTRACT_TEMPLATES, TIER_LIMITS, STORAGE_KEY, DEFAULT_MEDIA_KIT, DEFAULT_PROFILE } from './utils/constants';
import { formatCurrency, formatDate, formatRelativeDate, generateId, generateContractNumber, generateInvoiceNumber } from './utils/helpers';
import { 
  SponsyLogo, DollarSign, CheckCircle2, Plus, X, LayoutDashboard, Building, Receipt, 
  FileText, TrendingUp, Clock, Edit2, Save, Search, Mail, Star, ArrowLeft, User, Users, 
  Globe, Briefcase, Send, Check, Calendar, Lock, Settings, Trash2, ExternalLink, 
  AlertCircle, ChevronDown, FileCheck, LogOut, CreditCard, BarChart, Activity,
  Youtube, Twitch, Instagram, Twitter, Tiktok, getPlatformIcon
} from './components/icons';
import { Toast, Modal, EmptyState, Badge, Tabs, Button, Input, Select, Textarea, BrandLogo, StatCard, ProgressBar, LoadingScreen, ConfirmDialog } from './components/ui';
import AuthPage from './components/auth/AuthPage';

// ============ APP STATE CONTEXT ============
const AppStateContext = React.createContext(null);
const useAppState = () => React.useContext(AppStateContext);

function AppStateProvider({ children }) {
  const { user, isAuthenticated } = useAuth();
  
  const [deals, setDeals] = useState([]);
  const [brands, setBrands] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [contracts, setContracts] = useState([]);
  const [opportunities, setOpportunities] = useState([]);
  const [applications, setApplications] = useState([]);
  const [privateInvites, setPrivateInvites] = useState([]);
  const [mediaKit, setMediaKit] = useState(DEFAULT_MEDIA_KIT);
  const [profile, setProfile] = useState(DEFAULT_PROFILE);
  const [currentView, setCurrentView] = useState(() => {
    const savedView = localStorage.getItem('sponsy_current_view');
    return savedView || 'pipeline';
  });
  const [toast, setToast] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [dataSource, setDataSource] = useState('local'); // 'local' or 'supabase'
  const loadedRef = useRef(false);

  useEffect(() => {
    localStorage.setItem('sponsy_current_view', currentView);
  }, [currentView]);

  const showToast = useCallback((message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  }, []);

  // Load data from localStorage (simple, reliable)
  const loadFromLocalStorage = useCallback(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        const data = JSON.parse(saved);
        setDeals(data.deals || []);
        setBrands(data.brands || []);
        setContacts(data.contacts || []);
        setInvoices(data.invoices || []);
        setContracts(data.contracts || []);
        setOpportunities(data.opportunities || []);
        setApplications(data.applications || []);
        setPrivateInvites(data.privateInvites || []);
        setMediaKit(data.mediaKit || DEFAULT_MEDIA_KIT);
        setProfile(data.profile || DEFAULT_PROFILE);
      }
      setDataSource('local');
    } catch (e) {
      console.error('Failed to load from localStorage', e);
    }
  }, []);

  // Load data on mount - simplified to always work
  useEffect(() => {
    if (loadedRef.current) return;
    loadedRef.current = true;
    
    // Always start with localStorage for instant loading
    loadFromLocalStorage();
    setIsLoading(false);
    
    // If Supabase is enabled and user is logged in, try to sync in background
    if (user?.id && isSupabaseEnabled) {
      const syncFromSupabase = async () => {
        try {
          // Add timeout to prevent hanging
          const timeoutPromise = new Promise((_, reject) =>
            setTimeout(() => reject(new Error('Supabase timeout')), 5000)
          );
          
          const fetchPromise = Promise.all([
            db.getBrands(user.id),
            db.getDeals(user.id),
            db.getContracts(user.id),
            db.getInvoices(user.id),
          ]);
          
          const [brandsResult, dealsResult, contractsResult, invoicesResult] = await Promise.race([fetchPromise, timeoutPromise]);
          
          // Only use Supabase data if we got some
          const hasSupabaseData =
            (brandsResult.data?.length > 0) ||
            (dealsResult.data?.length > 0) ||
            (contractsResult.data?.length > 0) ||
            (invoicesResult.data?.length > 0);
            
          if (hasSupabaseData) {
            setBrands(brandsResult.data || []);
            setDeals(dealsResult.data || []);
            setContracts(contractsResult.data || []);
            setInvoices(invoicesResult.data || []);
            setDataSource('supabase');
            console.log('Synced data from Supabase');
          }
        } catch (e) {
          console.log('Supabase sync skipped:', e.message);
          // Keep using localStorage data - already loaded
        }
      };
      
      syncFromSupabase();
    }
  }, [user?.id, loadFromLocalStorage]);

  // Save to localStorage as backup (only if using local data source)
  useEffect(() => {
    if (isLoading || dataSource === 'supabase') return;
    const data = { deals, brands, contacts, invoices, contracts, opportunities, applications, privateInvites, mediaKit, profile };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  }, [deals, brands, contacts, invoices, contracts, opportunities, applications, privateInvites, mediaKit, profile, isLoading, dataSource]);

  // Deal operations
  const addDeal = useCallback(async (deal) => {
    if (dataSource === 'supabase' && user?.id) {
      const newDeal = {
        user_id: user.id,
        brand_id: deal.brandId || null,
        title: deal.title,
        value: Number(deal.value) || 0,
        stage: deal.stage || 'lead',
        platform: deal.platform,
        deliverables: deal.deliverables || [],
      };
      const { data, error } = await db.createDeal(newDeal);
      if (error) {
        showToast('Failed to create deal', 'error');
        return null;
      }
      setDeals(prev => [data, ...prev]);
      showToast('Deal created!');
      return data;
    } else {
      const newDeal = { ...deal, id: generateId(), createdAt: new Date().toISOString(), deliverables: deal.deliverables || [] };
      setDeals(prev => [...prev, newDeal]);
      showToast('Deal created!');
      return newDeal;
    }
  }, [showToast, dataSource, user?.id]);

  const updateDeal = useCallback(async (id, updates) => {
    if (dataSource === 'supabase') {
      const { error } = await db.updateDeal(id, updates);
      if (error) {
        showToast('Failed to update deal', 'error');
        return;
      }
    }
    setDeals(prev => prev.map(d => d.id === id ? { ...d, ...updates } : d));
  }, [dataSource, showToast]);

  const deleteDeal = useCallback(async (id) => {
    if (dataSource === 'supabase') {
      const { error } = await db.deleteDeal(id);
      if (error) {
        showToast('Failed to delete deal', 'error');
        return;
      }
    }
    setDeals(prev => prev.filter(d => d.id !== id));
    showToast('Deal deleted');
  }, [showToast, dataSource]);

  const moveDeal = useCallback(async (dealId, newStage) => {
    if (dataSource === 'supabase') {
      const { error } = await db.updateDeal(dealId, { stage: newStage });
      if (error) {
        showToast('Failed to move deal', 'error');
        return;
      }
    }
    setDeals(prev => prev.map(d => d.id === dealId ? { ...d, stage: newStage } : d));
  }, [dataSource, showToast]);

  const toggleDeliverable = useCallback((dealId, deliverableId) => {
    setDeals(prev => prev.map(d => d.id === dealId ? { ...d, deliverables: d.deliverables.map(del => del.id === deliverableId ? { ...del, completed: !del.completed } : del) } : d));
  }, []);

  const addDeliverable = useCallback((dealId, name) => {
    setDeals(prev => prev.map(d => d.id === dealId ? { ...d, deliverables: [...(d.deliverables || []), { id: generateId(), name, completed: false }] } : d));
  }, []);

  // Brand operations
  const addBrand = useCallback(async (brand) => {
    if (dataSource === 'supabase' && user?.id) {
      const newBrand = {
        user_id: user.id,
        name: brand.name,
        domain: brand.domain,
        industry: brand.industry,
        contact_name: brand.contactName,
        contact_email: brand.contactEmail,
        is_favorite: false,
      };
      const { data, error } = await db.createBrand(newBrand);
      if (error) {
        showToast('Failed to add brand', 'error');
        return null;
      }
      setBrands(prev => [data, ...prev]);
      showToast('Brand added!');
      return data;
    } else {
      const newBrand = { ...brand, id: generateId(), createdAt: new Date().toISOString() };
      setBrands(prev => [...prev, newBrand]);
      showToast('Brand added!');
      return newBrand;
    }
  }, [showToast, dataSource, user?.id]);

  const updateBrand = useCallback(async (id, updates) => {
    if (dataSource === 'supabase') {
      const { error } = await db.updateBrand(id, updates);
      if (error) {
        showToast('Failed to update brand', 'error');
        return;
      }
    }
    setBrands(prev => prev.map(b => b.id === id ? { ...b, ...updates } : b));
  }, [dataSource, showToast]);

  const deleteBrand = useCallback(async (id) => {
    if (dataSource === 'supabase') {
      const { error } = await db.deleteBrand(id);
      if (error) {
        showToast('Failed to delete brand', 'error');
        return;
      }
    }
    setBrands(prev => prev.filter(b => b.id !== id));
    setContacts(prev => prev.filter(c => c.brandId !== id));
    showToast('Brand deleted');
  }, [showToast, dataSource]);

  const toggleFavorite = useCallback(async (id) => {
    const brand = brands.find(b => b.id === id);
    const newValue = !brand?.favorite && !brand?.is_favorite;
    if (dataSource === 'supabase') {
      await db.updateBrand(id, { is_favorite: newValue });
    }
    setBrands(prev => prev.map(b => b.id === id ? { ...b, favorite: newValue, is_favorite: newValue } : b));
  }, [dataSource, brands]);

  // Contact operations
  const addContact = useCallback((contact) => { setContacts(prev => [...prev, { ...contact, id: generateId() }]); showToast('Contact added'); }, [showToast]);
  const updateContact = useCallback((id, updates) => setContacts(prev => prev.map(c => c.id === id ? { ...c, ...updates } : c)), []);
  const deleteContact = useCallback((id) => { setContacts(prev => prev.filter(c => c.id !== id)); showToast('Contact deleted'); }, [showToast]);

  // Invoice operations
  const addInvoice = useCallback(async (invoice) => {
    if (dataSource === 'supabase' && user?.id) {
      const newInvoice = {
        user_id: user.id,
        brand_id: invoice.brandId || null,
        amount: invoice.total || invoice.amount || 0,
        status: 'draft',
        line_items: invoice.items || [],
        due_date: invoice.dueDate,
        notes: invoice.notes,
      };
      const { data, error } = await db.createInvoice(newInvoice);
      if (error) {
        showToast('Failed to create invoice', 'error');
        return null;
      }
      setInvoices(prev => [data, ...prev]);
      showToast('Invoice created!');
      return data;
    } else {
      const newInvoice = { ...invoice, id: generateId(), number: generateInvoiceNumber(), createdAt: new Date().toISOString(), status: 'draft' };
      setInvoices(prev => [...prev, newInvoice]);
      showToast('Invoice created!');
      return newInvoice;
    }
  }, [showToast, dataSource, user?.id]);

  const updateInvoice = useCallback(async (id, updates) => {
    if (dataSource === 'supabase') {
      const { error } = await db.updateInvoice(id, updates);
      if (error) {
        showToast('Failed to update invoice', 'error');
        return;
      }
    }
    setInvoices(prev => prev.map(inv => inv.id === id ? { ...inv, ...updates } : inv));
  }, [dataSource, showToast]);

  const deleteInvoice = useCallback(async (id) => {
    if (dataSource === 'supabase') {
      const { error } = await db.deleteInvoice(id);
      if (error) {
        showToast('Failed to delete invoice', 'error');
        return;
      }
    }
    setInvoices(prev => prev.filter(inv => inv.id !== id));
    showToast('Invoice deleted');
  }, [showToast, dataSource]);

  // Contract operations
  const addContract = useCallback(async (contract) => {
    if (dataSource === 'supabase' && user?.id) {
      const newContract = {
        user_id: user.id,
        brand_id: contract.brandId || null,
        deal_id: contract.dealId || null,
        title: contract.title,
        content: contract.content,
        status: 'draft',
        history: [{ action: 'created', timestamp: new Date().toISOString(), by: 'creator' }]
      };
      const { data, error } = await db.createContract(newContract);
      if (error) {
        showToast('Failed to create contract', 'error');
        return null;
      }
      setContracts(prev => [data, ...prev]);
      showToast('Contract created!');
      return data;
    } else {
      const newContract = {
        ...contract,
        id: generateId(),
        number: generateContractNumber(),
        createdAt: new Date().toISOString(),
        status: 'draft',
        signatures: { creator: null, brand: null },
        history: [{ action: 'created', timestamp: new Date().toISOString(), by: 'creator' }]
      };
      setContracts(prev => [...prev, newContract]);
      showToast('Contract created!');
      return newContract;
    }
  }, [showToast, dataSource, user?.id]);

  const updateContract = useCallback(async (id, updates) => {
    if (dataSource === 'supabase') {
      const { error } = await db.updateContract(id, updates);
      if (error) {
        showToast('Failed to update contract', 'error');
        return;
      }
    }
    setContracts(prev => prev.map(con => con.id === id ? {
      ...con, ...updates,
      history: [...(con.history || []), { action: 'updated', timestamp: new Date().toISOString(), by: 'creator' }]
    } : con));
  }, [dataSource, showToast]);

  const deleteContract = useCallback(async (id) => {
    if (dataSource === 'supabase') {
      const { error } = await db.deleteContract(id);
      if (error) {
        showToast('Failed to delete contract', 'error');
        return;
      }
    }
    setContracts(prev => prev.filter(con => con.id !== id));
    showToast('Contract deleted');
  }, [showToast, dataSource]);

  const signContract = useCallback((id, party, signatureData) => {
    setContracts(prev => prev.map(con => {
      if (con.id !== id) return con;
      const newSignatures = { ...con.signatures, [party]: { data: signatureData, timestamp: new Date().toISOString() } };
      const bothSigned = newSignatures.creator && newSignatures.brand;
      return {
        ...con,
        signatures: newSignatures,
        status: bothSigned ? 'completed' : party === 'creator' ? 'signed-creator' : 'signed-brand',
        history: [...(con.history || []), { action: `signed_by_${party}`, timestamp: new Date().toISOString(), by: party }]
      };
    }));
    showToast(`Contract signed by ${party}!`);
  }, [showToast]);

  const sendContractForSignature = useCallback((id, recipientEmail) => {
    setContracts(prev => prev.map(con => con.id === id ? {
      ...con,
      status: 'sent',
      sentTo: recipientEmail,
      sentAt: new Date().toISOString(),
      history: [...(con.history || []), { action: 'sent', timestamp: new Date().toISOString(), to: recipientEmail }]
    } : con));
    showToast('Contract sent for signature!');
  }, [showToast]);

  // Opportunity operations
  const addOpportunity = useCallback((opp) => {
    const newOpp = { ...opp, id: generateId(), createdAt: new Date().toISOString(), status: 'open', applicantCount: 0 };
    setOpportunities(prev => [...prev, newOpp]);
    showToast('Opportunity created');
    return newOpp;
  }, [showToast]);

  const updateOpportunity = useCallback((id, updates) => setOpportunities(prev => prev.map(o => o.id === id ? { ...o, ...updates } : o)), []);
  const deleteOpportunity = useCallback((id) => { setOpportunities(prev => prev.filter(o => o.id !== id)); showToast('Opportunity deleted'); }, [showToast]);

  // Application operations
  const applyToOpportunity = useCallback((oppId, pitch) => {
    const newApp = { id: generateId(), opportunityId: oppId, pitch, createdAt: new Date().toISOString(), status: 'pending' };
    setApplications(prev => [...prev, newApp]);
    setOpportunities(prev => prev.map(o => o.id === oppId ? { ...o, applicantCount: (o.applicantCount || 0) + 1 } : o));
    showToast('Application submitted!');
    return newApp;
  }, [showToast]);

  const updateApplication = useCallback((id, updates) => setApplications(prev => prev.map(a => a.id === id ? { ...a, ...updates } : a)), []);

  // Private invite operations
  const sendPrivateInvite = useCallback((invite) => {
    const newInvite = { ...invite, id: generateId(), createdAt: new Date().toISOString(), status: 'pending' };
    setPrivateInvites(prev => [...prev, newInvite]);
    showToast('Invite sent');
    return newInvite;
  }, [showToast]);

  const respondToInvite = useCallback((id, response) => {
    setPrivateInvites(prev => prev.map(inv => inv.id === id ? { ...inv, status: response } : inv));
    if (response === 'accepted') showToast('Invite accepted! Deal added to pipeline.');
  }, [showToast]);

  // Getters
  const getBrand = useCallback((id) => brands.find(b => b.id === id), [brands]);
  const getBrandDeals = useCallback((id) => deals.filter(d => d.brandId === id), [deals]);
  const getBrandContacts = useCallback((id) => contacts.filter(c => c.brandId === id), [contacts]);
  const getDealContracts = useCallback((dealId) => contracts.filter(c => c.dealId === dealId), [contracts]);
  const getBrandContracts = useCallback((brandId) => contracts.filter(c => c.brandId === brandId), [contracts]);
  const getOpportunityApplications = useCallback((oppId) => applications.filter(a => a.opportunityId === oppId), [applications]);
  const getMyApplications = useCallback(() => applications, [applications]);
  const getMyInvites = useCallback(() => privateInvites, [privateInvites]);

  const getStats = useCallback(() => {
    const totalValue = deals.reduce((sum, d) => sum + (d.value || 0), 0);
    const paidValue = deals.filter(d => d.stage === 'paid').reduce((sum, d) => sum + (d.value || 0), 0);
    const pendingInvoicesData = invoices.filter(inv => ['sent', 'viewed'].includes(inv.status));
    const pendingContractsData = contracts.filter(c => ['draft', 'sent', 'signed-creator', 'signed-brand'].includes(c.status));
    return {
      totalDeals: deals.length,
      activeDeals: deals.filter(d => !['paid', 'lead'].includes(d.stage)).length,
      totalValue,
      paidValue,
      pendingValue: totalValue - paidValue,
      pendingInvoices: pendingInvoicesData.length,
      pendingAmount: pendingInvoicesData.reduce((sum, inv) => sum + (inv.total || 0), 0),
      openOpportunities: opportunities.filter(o => o.status === 'open').length,
      pendingApplications: applications.filter(a => a.status === 'pending').length,
      pendingInvites: privateInvites.filter(inv => inv.status === 'pending').length,
      pendingContracts: pendingContractsData.length,
      totalContracts: contracts.length,
    };
  }, [deals, invoices, contracts, opportunities, applications, privateInvites]);

  const getBrandStats = useCallback((brandId) => {
    const brandDeals = deals.filter(d => d.brandId === brandId);
    return {
      totalDeals: brandDeals.length,
      totalValue: brandDeals.reduce((sum, d) => sum + (d.value || 0), 0),
      paidValue: brandDeals.filter(d => d.stage === 'paid').reduce((sum, d) => sum + (d.value || 0), 0),
    };
  }, [deals]);

  const value = {
    // State
    deals, brands, contacts, invoices, contracts, opportunities, applications, privateInvites, mediaKit, profile, toast, isLoading,
    currentView, setCurrentView, showToast,
    // Deal operations
    addDeal, updateDeal, deleteDeal, moveDeal, toggleDeliverable, addDeliverable,
    // Brand operations
    addBrand, updateBrand, deleteBrand, toggleFavorite, getBrand, getBrandDeals, getBrandContacts, getBrandStats,
    // Contact operations
    addContact, updateContact, deleteContact,
    // Invoice operations
    addInvoice, updateInvoice, deleteInvoice,
    // Contract operations
    addContract, updateContract, deleteContract, signContract, sendContractForSignature, getDealContracts, getBrandContracts,
    // Opportunity operations
    addOpportunity, updateOpportunity, deleteOpportunity, applyToOpportunity, updateApplication, getOpportunityApplications, getMyApplications,
    // Invite operations
    sendPrivateInvite, respondToInvite, getMyInvites,
    // Other
    setMediaKit, setProfile, getStats,
  };

  return <AppStateContext.Provider value={value}>{children}</AppStateContext.Provider>;
}

// ============ SIDEBAR ============
function Sidebar({ isOpen, onClose }) {
  const { currentView, setCurrentView, getStats } = useAppState();
  const { logout, user, tier } = useAuth();
  const stats = getStats();
  
  const navItems = [
    { id: 'pipeline', label: 'Pipeline', icon: LayoutDashboard, badge: stats.activeDeals || null },
    { id: 'brands', label: 'Brands', icon: Building },
    { id: 'contracts', label: 'Contracts', icon: FileCheck, badge: stats.pendingContracts || null },
    { id: 'marketplace', label: 'Marketplace', icon: Briefcase, badge: stats.openOpportunities || null },
    { id: 'invites', label: 'Invites', icon: Mail, badge: stats.pendingInvites || null },
    { id: 'invoices', label: 'Invoices', icon: Receipt, badge: stats.pendingInvoices || null },
    { id: 'mediakit', label: 'Media Kit', icon: FileText },
    { id: 'analytics', label: 'Analytics', icon: BarChart },
  ];

  const handleNavClick = (id) => {
    setCurrentView(id);
    if (onClose) onClose(); // Close sidebar on mobile after navigation
  };

  return (
    <>
      {/* Mobile overlay */}
      {isOpen && (
        <div className="fixed inset-0 bg-black/60 z-40 lg:hidden" onClick={onClose} />
      )}
      
      {/* Sidebar */}
      <aside className={`
        fixed lg:relative inset-y-0 left-0 z-50
        w-64 lg:w-56 bg-neutral-950 border-r border-neutral-800/50
        flex flex-col shrink-0
        transform transition-transform duration-300 ease-in-out
        ${isOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
      `}>
        <div className="p-4 border-b border-neutral-800/50 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <SponsyLogo size={34} />
            <span className="text-xl font-bold text-white tracking-tight">Sponsy</span>
          </div>
          {/* Close button for mobile */}
          <button onClick={onClose} className="lg:hidden p-2 text-neutral-400 hover:text-white">
            <X size={20} />
          </button>
        </div>
        <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
          {navItems.map((item) => (
            <button key={item.id} onClick={() => handleNavClick(item.id)}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all ${
                currentView === item.id ? 'bg-lime-400 text-black font-semibold shadow-lg shadow-lime-400/30' : 'text-neutral-400 hover:bg-neutral-800/50 hover:text-white'
              }`}>
              <item.icon size={18} />
              <span className="flex-1 text-left">{item.label}</span>
              {item.badge > 0 && (
                <span className={`px-2 py-0.5 text-xs rounded-full font-medium ${currentView === item.id ? 'bg-white/20' : 'bg-neutral-800 text-neutral-300'}`}>
                  {item.badge}
                </span>
              )}
            </button>
          ))}
        </nav>
        <div className="p-3 border-t border-neutral-800/50 space-y-2">
          <button onClick={() => handleNavClick('settings')} className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl transition-colors ${currentView === 'settings' ? 'bg-lime-400 text-black' : 'hover:bg-neutral-800/50 text-neutral-400 hover:text-white'}`}>
            <Settings size={18} />
            <span className="flex-1 text-left text-sm font-medium">Settings</span>
          </button>
          <div className="flex items-center gap-3 px-3 py-2.5">
            <div className="w-9 h-9 rounded-xl bg-lime-400 flex items-center justify-center">
              <User size={18} className="text-black" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-white truncate">{user?.email?.split('@')[0] || 'User'}</p>
              <p className="text-xs text-neutral-500 capitalize">{tier} Plan</p>
            </div>
          </div>
        </div>
      </aside>
    </>
  );
}

// ============ STATS COMPONENT ============
function Stats() {
  const { getStats } = useAppState();
  const stats = getStats();
  
  const cards = [
    { label: 'Pipeline', value: formatCurrency(stats.totalValue), icon: DollarSign, color: 'text-emerald-400', gradient: 'from-emerald-600/20 to-emerald-600/5' },
    { label: 'Earned', value: formatCurrency(stats.paidValue), icon: CheckCircle2, color: 'text-green-400', gradient: 'from-green-600/20 to-green-600/5' },
    { label: 'Pending', value: formatCurrency(stats.pendingValue), icon: Clock, color: 'text-amber-400', gradient: 'from-amber-600/20 to-amber-600/5' },
    { label: 'Active', value: stats.activeDeals, icon: TrendingUp, color: 'text-lime-400', gradient: 'from-lime-400/20 to-lime-400/5' },
  ];
  
  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 lg:gap-4 mb-6">
      {cards.map((c) => (
        <StatCard key={c.label} label={c.label} value={c.value} icon={c.icon} color={c.color} gradient={c.gradient} />
      ))}
    </div>
  );
}

// ============ DEAL CARD ============
function DealCard({ deal, onClick }) {
  const { getBrand } = useAppState();
  const brand = getBrand(deal.brandId);
  const progress = deal.deliverables?.length > 0 ? Math.round((deal.deliverables.filter(d => d.completed).length / deal.deliverables.length) * 100) : null;
  
  return (
    <div onClick={() => onClick(deal)} className="bg-neutral-800/50 rounded-xl p-3.5 cursor-pointer hover:bg-neutral-800 transition-all border border-neutral-700/30 hover:border-neutral-600/50 group">
      <div className="flex items-start gap-3">
        <BrandLogo domain={brand?.domain} name={brand?.name || deal.brandName} size={38} />
        <div className="flex-1 min-w-0">
          <p className="text-xs text-neutral-500 truncate">{brand?.name || deal.brandName}</p>
          <h4 className="text-sm text-white font-medium truncate leading-tight">{deal.title}</h4>
          <div className="flex items-center gap-2 mt-2">
            <span className="text-emerald-400 font-bold text-sm">{formatCurrency(deal.value)}</span>
            {deal.platform && <span className="text-xs text-neutral-500">· {deal.platform}</span>}
          </div>
          {progress !== null && (
            <div className="mt-2.5">
              <ProgressBar value={progress} showLabel />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ============ KANBAN COLUMN ============
function KanbanColumn({ stage, deals, onDealClick, onDrop }) {
  const total = deals.reduce((sum, d) => sum + (d.value || 0), 0);
  const [isOver, setIsOver] = useState(false);
  
  return (
    <div className="flex flex-col min-w-[220px] max-w-[220px] lg:min-w-[260px] lg:max-w-[260px]">
      <div className={`flex items-center justify-between px-3 lg:px-4 py-2 lg:py-2.5 rounded-t-xl ${stage.color}`}>
        <div className="flex items-center gap-1.5 lg:gap-2">
          <h3 className="font-semibold text-white text-xs lg:text-sm">{stage.name}</h3>
          <span className="px-1.5 lg:px-2 py-0.5 text-xs bg-black/20 text-white/90 rounded-full font-medium">{deals.length}</span>
        </div>
        <span className="text-xs text-white/70 font-medium hidden sm:block">{formatCurrency(total)}</span>
      </div>
      <div
        onDragOver={(e) => { e.preventDefault(); setIsOver(true); }}
        onDragLeave={() => setIsOver(false)}
        onDrop={(e) => { e.preventDefault(); setIsOver(false); onDrop(e.dataTransfer.getData('dealId'), stage.id); }}
        className={`flex-1 p-1.5 lg:p-2 space-y-2 bg-neutral-900/30 rounded-b-xl border border-t-0 border-neutral-800/50 min-h-[140px] lg:min-h-[160px] transition-all ${isOver ? 'bg-neutral-800/30 border-lime-400/30 ring-2 ring-lime-400/20' : ''}`}
      >
        {deals.map((deal) => (
          <div key={deal.id} draggable onDragStart={(e) => e.dataTransfer.setData('dealId', deal.id)}>
            <DealCard deal={deal} onClick={onDealClick} />
          </div>
        ))}
      </div>
    </div>
  );
}

// ============ NEW DEAL MODAL ============
function NewDealModal({ onClose, preselectedBrand }) {
  const { addDeal, brands } = useAppState();
  const [deal, setDeal] = useState({ brandId: preselectedBrand?.id || '', title: '', value: '', stage: 'lead', platform: '', dealType: '', deliverables: [] });

  const handleSubmit = () => {
    if (deal.brandId && deal.title) {
      addDeal({ ...deal, value: Number(deal.value) || 0 });
      onClose();
    }
  };

  return (
    <Modal title="New Deal" onClose={onClose}>
      <div className="p-6 space-y-4">
        <Select label="Brand" value={deal.brandId} onChange={(e) => setDeal({ ...deal, brandId: e.target.value })} options={brands.map(b => ({ value: b.id, label: b.name }))} placeholder="Select brand..." />
        {brands.length === 0 && <p className="text-xs text-amber-400 -mt-2">Add brands first in the Brands section</p>}
        <Input label="Deal Title" value={deal.title} onChange={(e) => setDeal({ ...deal, title: e.target.value })} placeholder="e.g., Q1 Integration Campaign" />
        <div className="grid grid-cols-2 gap-4">
          <Input label="Value ($)" type="number" value={deal.value} onChange={(e) => setDeal({ ...deal, value: e.target.value })} placeholder="0" />
          <Select label="Platform" value={deal.platform} onChange={(e) => setDeal({ ...deal, platform: e.target.value })} options={PLATFORMS.map(p => ({ value: p.name, label: p.name }))} />
        </div>
      </div>
      <div className="border-t border-neutral-800 px-6 py-4 flex justify-end gap-3">
        <Button variant="secondary" onClick={onClose}>Cancel</Button>
        <Button onClick={handleSubmit} disabled={!deal.brandId || !deal.title}>Create Deal</Button>
      </div>
    </Modal>
  );
}

// ============ DEAL MODAL ============
function DealModal({ deal, onClose }) {
  const { updateDeal, deleteDeal, toggleDeliverable, addDeliverable, getBrand, brands } = useAppState();
  const [isEditing, setIsEditing] = useState(!deal.id);
  const [edited, setEdited] = useState(deal);
  const [newDel, setNewDel] = useState('');
  
  const brand = getBrand(deal.brandId);
  const stage = STAGES.find(s => s.id === deal.stage);

  const handleSave = () => { if (deal.id) updateDeal(deal.id, edited); setIsEditing(false); };
  const handleDelete = () => { deleteDeal(deal.id); onClose(); };
  const handleAddDel = () => { if (newDel.trim()) { addDeliverable(deal.id, newDel.trim()); setNewDel(''); } };

  return (
    <Modal title={isEditing ? 'Edit Deal' : deal.title} onClose={onClose}>
      <div className="p-6 space-y-5">
        <div className="flex items-center gap-4">
          <BrandLogo domain={brand?.domain} name={brand?.name || edited.brandName} size={56} />
          <div className="flex-1">
            {isEditing ? (
              <Select value={edited.brandId || ''} onChange={(e) => setEdited({ ...edited, brandId: e.target.value })} options={brands.map(b => ({ value: b.id, label: b.name }))} placeholder="Select brand..." className="mb-2" />
            ) : (
              <p className="text-neutral-400 text-sm">{brand?.name}</p>
            )}
            {isEditing ? (
              <Input value={edited.title || ''} onChange={(e) => setEdited({ ...edited, title: e.target.value })} placeholder="Deal title" className="font-semibold" />
            ) : (
              <h3 className="text-xl font-bold text-white">{deal.title}</h3>
            )}
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-xs text-neutral-500 mb-1.5 font-medium">Stage</label>
            {isEditing ? (
              <Select value={edited.stage || 'lead'} onChange={(e) => setEdited({ ...edited, stage: e.target.value })} options={STAGES.map(s => ({ value: s.id, label: s.name }))} />
            ) : (
              <div className={`inline-flex px-3 py-1.5 rounded-xl text-white text-sm font-medium ${stage?.color}`}>{stage?.name}</div>
            )}
          </div>
          <div>
            <label className="block text-xs text-neutral-500 mb-1.5 font-medium">Value</label>
            {isEditing ? (
              <Input type="number" value={edited.value || ''} onChange={(e) => setEdited({ ...edited, value: Number(e.target.value) })} placeholder="0" />
            ) : (
              <p className="text-2xl font-bold text-emerald-400">{formatCurrency(deal.value)}</p>
            )}
          </div>
        </div>

        {isEditing && (
          <div className="grid grid-cols-2 gap-4">
            <Select label="Platform" value={edited.platform || ''} onChange={(e) => setEdited({ ...edited, platform: e.target.value })} options={PLATFORMS.map(p => ({ value: p.name, label: p.name }))} />
            <Select label="Deal Type" value={edited.dealType || ''} onChange={(e) => setEdited({ ...edited, dealType: e.target.value })} options={DEAL_TYPES.map(t => ({ value: t, label: t }))} />
          </div>
        )}

        {!isEditing && (
          <div>
            <div className="flex items-center justify-between mb-3">
              <label className="text-sm font-medium text-white">Deliverables</label>
              <span className="text-xs text-neutral-500">{deal.deliverables?.filter(d => d.completed).length || 0}/{deal.deliverables?.length || 0} completed</span>
            </div>
            <div className="space-y-2 mb-3">
              {deal.deliverables?.map((d) => (
                <div key={d.id} className="flex items-center gap-3 p-3 bg-neutral-800/50 rounded-xl group">
                  <button onClick={() => toggleDeliverable(deal.id, d.id)} className={`w-5 h-5 rounded-lg border-2 flex items-center justify-center transition-colors ${d.completed ? 'bg-emerald-500 border-emerald-500' : 'border-neutral-600 hover:border-neutral-500'}`}>
                    {d.completed && <Check size={12} className="text-white" />}
                  </button>
                  <span className={`text-sm flex-1 ${d.completed ? 'text-neutral-500 line-through' : 'text-white'}`}>{d.name}</span>
                </div>
              ))}
            </div>
            <div className="flex gap-2">
              <Input value={newDel} onChange={(e) => setNewDel(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && handleAddDel()} placeholder="Add deliverable..." className="flex-1" />
              <Button onClick={handleAddDel} icon={Plus} />
            </div>
          </div>
        )}
      </div>

      <div className="sticky bottom-0 bg-neutral-900 border-t border-neutral-800 px-6 py-4 flex justify-between rounded-b-2xl">
        {deal.id && <Button variant="ghost" onClick={handleDelete} className="text-red-400 hover:text-red-300">Delete Deal</Button>}
        <div className="flex gap-2 ml-auto">
          {isEditing ? (
            <>
              <Button variant="secondary" onClick={() => { setEdited(deal); setIsEditing(false); }}>Cancel</Button>
              <Button onClick={handleSave} icon={Save}>Save</Button>
            </>
          ) : (
            <Button variant="secondary" onClick={() => setIsEditing(true)} icon={Edit2}>Edit</Button>
          )}
        </div>
      </div>
    </Modal>
  );
}

// ============ PIPELINE VIEW ============
function PipelineView() {
  const { deals, moveDeal } = useAppState();
  const [selectedDeal, setSelectedDeal] = useState(null);
  const [showNewDeal, setShowNewDeal] = useState(false);

  const dealsByStage = useMemo(() => STAGES.reduce((acc, stage) => {
    acc[stage.id] = deals.filter(d => d.stage === stage.id);
    return acc;
  }, {}), [deals]);

  return (
    <div className="flex-1 flex flex-col overflow-hidden bg-neutral-950">
      <header className="px-4 lg:px-6 py-4 lg:py-5 border-b border-neutral-800/50 shrink-0">
        <div className="flex items-center justify-between gap-3">
          <div className="min-w-0">
            <h1 className="text-xl lg:text-2xl font-bold text-white truncate">Deal Pipeline</h1>
            <p className="text-xs lg:text-sm text-neutral-500 mt-0.5 hidden sm:block">Track and manage your sponsorship deals</p>
          </div>
          <Button onClick={() => setShowNewDeal(true)} icon={Plus} className="shrink-0">
            <span className="hidden sm:inline">New Deal</span>
            <span className="sm:hidden">Add</span>
          </Button>
        </div>
      </header>
      <main className="flex-1 overflow-auto p-4 lg:p-6">
        <Stats />
        {deals.length === 0 ? (
          <EmptyState icon={LayoutDashboard} title="No deals yet" description="Start tracking your brand partnerships by adding your first deal." action="Add First Deal" onAction={() => setShowNewDeal(true)} />
        ) : (
          <div className="flex gap-3 lg:gap-4 overflow-x-auto pb-4 -mx-4 px-4 lg:mx-0 lg:px-0">
            {STAGES.map((stage) => (
              <KanbanColumn key={stage.id} stage={stage} deals={dealsByStage[stage.id] || []} onDealClick={setSelectedDeal} onDrop={moveDeal} />
            ))}
          </div>
        )}
      </main>
      {selectedDeal && <DealModal deal={deals.find(d => d.id === selectedDeal.id) || selectedDeal} onClose={() => setSelectedDeal(null)} />}
      {showNewDeal && <NewDealModal onClose={() => setShowNewDeal(false)} />}
    </div>
  );
}

// ============ BRANDS VIEW ============
function BrandsView() {
  const { brands, addBrand, getBrandStats, toggleFavorite, showToast } = useAppState();
  const [selectedBrand, setSelectedBrand] = useState(null);
  const [showNewBrand, setShowNewBrand] = useState(false);
  const [search, setSearch] = useState('');
  const [newBrand, setNewBrand] = useState({ name: '', domain: '', industry: 'Other' });

  const filtered = brands.filter(b => b.name.toLowerCase().includes(search.toLowerCase()));

  if (selectedBrand) {
    return <BrandDetail brand={brands.find(b => b.id === selectedBrand) || {}} onBack={() => setSelectedBrand(null)} />;
  }

  const handleAddBrand = () => {
    if (newBrand.name) {
      addBrand(newBrand);
      setNewBrand({ name: '', domain: '', industry: 'Other' });
      setShowNewBrand(false);
    }
  };

  return (
    <div className="flex-1 flex flex-col overflow-hidden bg-neutral-950">
      <header className="px-4 lg:px-6 py-4 lg:py-5 border-b border-neutral-800/50">
        <div className="flex items-center justify-between gap-3">
          <div className="min-w-0">
            <h1 className="text-xl lg:text-2xl font-bold text-white">Brands & Contacts</h1>
            <p className="text-xs lg:text-sm text-neutral-500 mt-0.5 hidden sm:block">Manage your brand relationships</p>
          </div>
          <Button onClick={() => setShowNewBrand(true)} icon={Plus} className="shrink-0">
            <span className="hidden sm:inline">Add Brand</span>
            <span className="sm:hidden">Add</span>
          </Button>
        </div>
      </header>
      <div className="px-4 lg:px-6 py-3 border-b border-neutral-800/50">
        <div className="relative">
          <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-neutral-500" />
          <input type="text" placeholder="Search brands..." value={search} onChange={(e) => setSearch(e.target.value)} className="w-full bg-neutral-900 border border-neutral-800 rounded-xl pl-11 pr-4 py-2.5 text-white text-sm placeholder-neutral-600" />
        </div>
      </div>
      <main className="flex-1 overflow-auto p-4 lg:p-6">
        {brands.length === 0 ? (
          <EmptyState icon={Building} title="No brands yet" description="Add brands you work with to track contacts and deals." action="Add First Brand" onAction={() => setShowNewBrand(true)} />
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 lg:gap-4">
            {filtered.map((brand) => {
              const stats = getBrandStats(brand.id);
              return (
                <div key={brand.id} onClick={() => setSelectedBrand(brand.id)} className="bg-neutral-900/50 rounded-2xl p-5 cursor-pointer hover:bg-neutral-900 transition-all border border-neutral-800/50 hover:border-neutral-700/50 group">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <BrandLogo domain={brand.domain} name={brand.name} size={48} />
                      <div>
                        <h3 className="text-white font-semibold">{brand.name}</h3>
                        <p className="text-xs text-neutral-500">{brand.industry}</p>
                      </div>
                    </div>
                    <button onClick={(e) => { e.stopPropagation(); toggleFavorite(brand.id); }} className={`p-1.5 rounded-lg transition-colors ${brand.favorite ? 'text-amber-400' : 'text-neutral-600 hover:text-neutral-400'}`}>
                      <Star size={18} fill={brand.favorite ? 'currentColor' : 'none'} />
                    </button>
                  </div>
                  <div className="grid grid-cols-3 gap-3">
                    <div><p className="text-xs text-neutral-500">Deals</p><p className="text-lg font-bold text-white">{stats.totalDeals}</p></div>
                    <div><p className="text-xs text-neutral-500">Total</p><p className="text-lg font-bold text-emerald-400">{formatCurrency(stats.totalValue)}</p></div>
                    <div><p className="text-xs text-neutral-500">Earned</p><p className="text-lg font-bold text-green-400">{formatCurrency(stats.paidValue)}</p></div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </main>
      {showNewBrand && (
        <Modal title="Add Brand" onClose={() => setShowNewBrand(false)} size="sm">
          <div className="p-6 space-y-4">
            <Input label="Brand Name" value={newBrand.name} onChange={(e) => setNewBrand({ ...newBrand, name: e.target.value })} placeholder="e.g., Razer" />
            <div>
              <Input label="Website Domain" value={newBrand.domain} onChange={(e) => setNewBrand({ ...newBrand, domain: e.target.value })} placeholder="razer.com" />
              <p className="text-xs text-neutral-500 mt-1">Used to fetch brand logo</p>
            </div>
            <Select label="Industry" value={newBrand.industry} onChange={(e) => setNewBrand({ ...newBrand, industry: e.target.value })} options={INDUSTRIES} />
            {newBrand.domain && <div className="flex items-center gap-3 p-3 bg-neutral-800/50 rounded-xl"><BrandLogo domain={newBrand.domain} name={newBrand.name} size={40} /><span className="text-sm text-neutral-400">Logo preview</span></div>}
          </div>
          <div className="border-t border-neutral-800 px-6 py-4 flex justify-end gap-3">
            <Button variant="secondary" onClick={() => setShowNewBrand(false)}>Cancel</Button>
            <Button onClick={handleAddBrand} disabled={!newBrand.name}>Add Brand</Button>
          </div>
        </Modal>
      )}
    </div>
  );
}

// ============ BRAND DETAIL ============
function BrandDetail({ brand, onBack }) {
  const { getBrandDeals, getBrandContacts, getBrandStats, addContact, deleteContact } = useAppState();
  const deals = getBrandDeals(brand.id);
  const contacts = getBrandContacts(brand.id);
  const stats = getBrandStats(brand.id);
  const [showAddContact, setShowAddContact] = useState(false);
  const [newContact, setNewContact] = useState({ name: '', role: '', email: '', phone: '' });

  const handleAddContact = () => {
    if (newContact.name && newContact.email) {
      addContact({ ...newContact, brandId: brand.id });
      setNewContact({ name: '', role: '', email: '', phone: '' });
      setShowAddContact(false);
    }
  };

  return (
    <div className="flex-1 flex flex-col overflow-hidden bg-neutral-950">
      <header className="px-6 py-5 border-b border-neutral-800/50">
        <div className="flex items-center gap-4">
          <button onClick={onBack} className="p-2 hover:bg-neutral-800 rounded-xl text-neutral-400 hover:text-white"><ArrowLeft size={20} /></button>
          <BrandLogo domain={brand.domain} name={brand.name} size={48} />
          <div className="flex-1">
            <h1 className="text-xl font-bold text-white">{brand.name}</h1>
            <p className="text-sm text-neutral-500">{brand.industry}</p>
          </div>
        </div>
      </header>
      <main className="flex-1 overflow-auto p-6">
        <div className="grid grid-cols-3 gap-4 mb-8">
          <div className="bg-neutral-900/50 border border-neutral-800/50 rounded-2xl p-5"><p className="text-xs text-neutral-500 mb-1">Total Deals</p><p className="text-3xl font-bold text-white">{stats.totalDeals}</p></div>
          <div className="bg-neutral-900/50 border border-neutral-800/50 rounded-2xl p-5"><p className="text-xs text-neutral-500 mb-1">Total Value</p><p className="text-3xl font-bold text-emerald-400">{formatCurrency(stats.totalValue)}</p></div>
          <div className="bg-neutral-900/50 border border-neutral-800/50 rounded-2xl p-5"><p className="text-xs text-neutral-500 mb-1">Earned</p><p className="text-3xl font-bold text-green-400">{formatCurrency(stats.paidValue)}</p></div>
        </div>
        <div className="grid grid-cols-2 gap-8">
          <div>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-white">Contacts</h2>
              <button onClick={() => setShowAddContact(true)} className="text-sm text-lime-400 hover:text-lime-300 flex items-center gap-1"><Plus size={16} />Add</button>
            </div>
            {contacts.length === 0 ? <p className="text-sm text-neutral-500 py-8 text-center">No contacts yet</p> : (
              <div className="space-y-3">
                {contacts.map(c => (
                  <div key={c.id} className="bg-neutral-900/50 border border-neutral-800/50 rounded-xl p-4 group">
                    <div className="flex items-center justify-between mb-2">
                      <div><p className="text-white font-medium">{c.name}</p><p className="text-xs text-neutral-500">{c.role}</p></div>
                      <button onClick={() => deleteContact(c.id)} className="p-1 text-neutral-600 hover:text-red-400 opacity-0 group-hover:opacity-100"><Trash2 size={14} /></button>
                    </div>
                    <a href={`mailto:${c.email}`} className="text-sm text-neutral-400 hover:text-lime-400 flex items-center gap-2"><Mail size={14} />{c.email}</a>
                  </div>
                ))}
              </div>
            )}
            {showAddContact && (
              <div className="mt-4 bg-neutral-900/50 border border-neutral-800/50 rounded-xl p-4 space-y-3">
                <Input placeholder="Name" value={newContact.name} onChange={(e) => setNewContact({ ...newContact, name: e.target.value })} />
                <Input placeholder="Role" value={newContact.role} onChange={(e) => setNewContact({ ...newContact, role: e.target.value })} />
                <Input placeholder="Email" type="email" value={newContact.email} onChange={(e) => setNewContact({ ...newContact, email: e.target.value })} />
                <div className="flex justify-end gap-2">
                  <Button variant="ghost" size="sm" onClick={() => setShowAddContact(false)}>Cancel</Button>
                  <Button size="sm" onClick={handleAddContact}>Add</Button>
                </div>
              </div>
            )}
          </div>
          <div>
            <h2 className="text-lg font-semibold text-white mb-4">Deal History</h2>
            {deals.length === 0 ? <p className="text-sm text-neutral-500 py-8 text-center">No deals yet</p> : (
              <div className="space-y-3">
                {deals.map(d => {
                  const stage = STAGES.find(s => s.id === d.stage);
                  return (
                    <div key={d.id} className="bg-neutral-900/50 border border-neutral-800/50 rounded-xl p-4">
                      <div className="flex items-start justify-between mb-2">
                        <div><p className="text-white font-medium">{d.title}</p><p className="text-xs text-neutral-500">{d.platform}</p></div>
                        <span className={`text-xs px-2 py-0.5 rounded-lg ${stage?.color} text-white`}>{stage?.name}</span>
                      </div>
                      <p className="text-emerald-400 font-bold">{formatCurrency(d.value)}</p>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}

// ============ INVOICES VIEW (Simplified) ============
function InvoicesView() {
  const { invoices, addInvoice, updateInvoice, brands, showToast } = useAppState();
  const [showNew, setShowNew] = useState(false);
  const [filter, setFilter] = useState('all');

  const filtered = filter === 'all' ? invoices : invoices.filter(inv => inv.status === filter);

  return (
    <div className="flex-1 flex flex-col overflow-hidden bg-neutral-950">
      <header className="px-4 lg:px-6 py-4 lg:py-5 border-b border-neutral-800/50">
        <div className="flex items-center justify-between gap-3">
          <div className="min-w-0">
            <h1 className="text-xl lg:text-2xl font-bold text-white">Invoices</h1>
            <p className="text-xs lg:text-sm text-neutral-500 mt-0.5 hidden sm:block">Create and track payment for your work</p>
          </div>
          <Button onClick={() => setShowNew(true)} icon={Plus} className="shrink-0">
            <span className="hidden sm:inline">New Invoice</span>
            <span className="sm:hidden">New</span>
          </Button>
        </div>
      </header>
      <div className="px-4 lg:px-6 py-3 border-b border-neutral-800/50 flex gap-2 overflow-x-auto">
        {['all', ...INVOICE_STATUS.map(s => s.id)].map(f => (
          <button key={f} onClick={() => setFilter(f)} className={`px-4 py-2 rounded-xl text-sm font-medium whitespace-nowrap transition-colors ${filter === f ? 'bg-neutral-800 text-white' : 'text-neutral-500 hover:text-white'}`}>
            {f === 'all' ? 'All' : INVOICE_STATUS.find(s => s.id === f)?.name} ({f === 'all' ? invoices.length : invoices.filter(i => i.status === f).length})
          </button>
        ))}
      </div>
      <main className="flex-1 overflow-auto p-4 lg:p-6">
        {invoices.length === 0 ? (
          <EmptyState icon={Receipt} title="No invoices yet" description="Create professional invoices for your completed work." action="Create Invoice" onAction={() => setShowNew(true)} />
        ) : (
          <div className="space-y-3">
            {filtered.map(inv => {
              const brand = brands.find(b => b.id === inv.brandId);
              const status = INVOICE_STATUS.find(s => s.id === inv.status);
              return (
                <div key={inv.id} className="bg-neutral-900/50 border border-neutral-800/50 rounded-xl p-4 lg:p-5">
                  <div className="flex items-start gap-3 lg:gap-4">
                    <BrandLogo domain={brand?.domain} name={brand?.name || inv.clientName} size={40} className="hidden sm:block" />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <span className="text-white font-semibold text-sm lg:text-base">{inv.number}</span>
                        <span className={`px-2 py-0.5 text-xs rounded-lg ${status?.color} text-white`}>{status?.name}</span>
                      </div>
                      <p className="text-xs lg:text-sm text-neutral-400 truncate">{brand?.name || inv.clientName}</p>
                    </div>
                    <div className="text-right shrink-0">
                      <p className="text-lg lg:text-xl font-bold text-white">{formatCurrency(inv.total)}</p>
                      <p className="text-xs text-neutral-500">Due {formatDate(inv.dueDate)}</p>
                    </div>
                  </div>
                  <div className="flex gap-2 mt-3 lg:mt-0 lg:absolute lg:right-5 lg:top-1/2 lg:-translate-y-1/2">
                    {inv.status === 'draft' && (
                      <Button size="sm" icon={Send} onClick={() => { updateInvoice(inv.id, { status: 'sent' }); showToast('Invoice sent!'); }} className="flex-1 lg:flex-none">Send</Button>
                    )}
                    {inv.status === 'sent' && (
                      <Button size="sm" variant="secondary" onClick={() => { updateInvoice(inv.id, { status: 'paid' }); showToast('Marked as paid!'); }} className="flex-1 lg:flex-none">Mark Paid</Button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </main>
      {showNew && <NewInvoiceModal onClose={() => setShowNew(false)} />}
    </div>
  );
}

// ============ NEW INVOICE MODAL ============
function NewInvoiceModal({ onClose }) {
  const { addInvoice, brands } = useAppState();
  const [inv, setInv] = useState({ brandId: '', items: [{ description: '', quantity: 1, rate: 0 }], dueDate: '', notes: '', tax: 0 });

  const subtotal = inv.items.reduce((sum, i) => sum + i.quantity * i.rate, 0);
  const taxAmount = subtotal * (inv.tax / 100);
  const total = subtotal + taxAmount;

  const addItem = () => setInv({ ...inv, items: [...inv.items, { description: '', quantity: 1, rate: 0 }] });
  const updateItem = (idx, field, value) => {
    const items = [...inv.items];
    items[idx][field] = ['quantity', 'rate'].includes(field) ? Number(value) : value;
    setInv({ ...inv, items });
  };

  const handleSubmit = () => {
    if (inv.brandId && inv.items.some(i => i.description)) {
      addInvoice({ ...inv, subtotal, taxAmount, total });
      onClose();
    }
  };

  return (
    <Modal title="Create Invoice" onClose={onClose} size="lg">
      <div className="p-6 space-y-5">
        <div className="grid grid-cols-2 gap-4">
          <Select label="Bill To" value={inv.brandId} onChange={(e) => setInv({ ...inv, brandId: e.target.value })} options={brands.map(b => ({ value: b.id, label: b.name }))} placeholder="Select brand..." />
          <Input label="Due Date" type="date" value={inv.dueDate} onChange={(e) => setInv({ ...inv, dueDate: e.target.value })} />
        </div>
        <div>
          <div className="flex items-center justify-between mb-3">
            <label className="text-sm font-medium text-white">Line Items</label>
            <button onClick={addItem} className="text-sm text-lime-400 hover:text-lime-300 flex items-center gap-1"><Plus size={14} />Add</button>
          </div>
          <div className="space-y-2">
            {inv.items.map((item, idx) => (
              <div key={idx} className="grid grid-cols-12 gap-2">
                <Input value={item.description} onChange={(e) => updateItem(idx, 'description', e.target.value)} placeholder="Description" className="col-span-6" />
                <Input type="number" value={item.quantity} onChange={(e) => updateItem(idx, 'quantity', e.target.value)} className="col-span-2 text-center" />
                <Input type="number" value={item.rate || ''} onChange={(e) => updateItem(idx, 'rate', e.target.value)} placeholder="Rate" className="col-span-3" />
                <div className="col-span-1 flex items-center justify-end text-white font-medium text-sm">{formatCurrency(item.quantity * item.rate)}</div>
              </div>
            ))}
          </div>
        </div>
        <div className="bg-neutral-800/50 rounded-xl p-4 space-y-2">
          <div className="flex justify-between text-sm"><span className="text-neutral-400">Subtotal</span><span className="text-white">{formatCurrency(subtotal)}</span></div>
          <div className="flex justify-between items-center text-sm">
            <span className="text-neutral-400">Tax</span>
            <div className="flex items-center gap-2">
              <input type="number" value={inv.tax || ''} onChange={(e) => setInv({ ...inv, tax: Number(e.target.value) })} className="w-16 bg-neutral-700 border border-neutral-600 rounded-lg px-2 py-1 text-white text-sm text-right" placeholder="0" />
              <span className="text-neutral-400">%</span>
              <span className="text-white w-20 text-right">{formatCurrency(taxAmount)}</span>
            </div>
          </div>
          <div className="flex justify-between text-lg font-bold border-t border-neutral-700 pt-2 mt-2"><span className="text-white">Total</span><span className="text-lime-400">{formatCurrency(total)}</span></div>
        </div>
      </div>
      <div className="border-t border-neutral-800 px-6 py-4 flex justify-end gap-3">
        <Button variant="secondary" onClick={onClose}>Cancel</Button>
        <Button onClick={handleSubmit} disabled={!inv.brandId || !inv.items.some(i => i.description)}>Create Invoice</Button>
      </div>
    </Modal>
  );
}

// ============ CONTRACTS VIEW (Simplified) ============
function ContractsView() {
  const { contracts } = useAppState();
  const [showNew, setShowNew] = useState(false);
  const [filter, setFilter] = useState('all');

  const filtered = filter === 'all' ? contracts : contracts.filter(c => c.status === filter);

  return (
    <div className="flex-1 flex flex-col overflow-hidden bg-neutral-950">
      <header className="px-4 lg:px-6 py-4 lg:py-5 border-b border-neutral-800/50">
        <div className="flex items-center justify-between gap-3">
          <div className="min-w-0">
            <h1 className="text-xl lg:text-2xl font-bold text-white">Contracts</h1>
            <p className="text-xs lg:text-sm text-neutral-500 mt-0.5 hidden sm:block">Manage your sponsorship agreements</p>
          </div>
          <Button onClick={() => setShowNew(true)} icon={Plus} className="shrink-0">
            <span className="hidden sm:inline">New Contract</span>
            <span className="sm:hidden">New</span>
          </Button>
        </div>
      </header>
      <main className="flex-1 overflow-auto p-4 lg:p-6">
        {contracts.length === 0 ? (
          <EmptyState icon={FileCheck} title="No contracts yet" description="Create and manage your sponsorship contracts." action="Create Contract" onAction={() => setShowNew(true)} />
        ) : (
          <div className="space-y-3">
            {filtered.map(contract => {
              const status = CONTRACT_STATUS.find(s => s.id === contract.status);
              return (
                <div key={contract.id} className="bg-neutral-900/50 border border-neutral-800/50 rounded-xl p-5">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-white font-semibold">{contract.number}</span>
                        <span className={`px-2 py-0.5 text-xs rounded-lg ${status?.color} text-white`}>{status?.name}</span>
                      </div>
                      <p className="text-sm text-neutral-400">{contract.title}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-lg font-bold text-emerald-400">{formatCurrency(contract.value)}</p>
                      <p className="text-xs text-neutral-500">{formatDate(contract.createdAt)}</p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </main>
      {showNew && <NewContractModal onClose={() => setShowNew(false)} />}
    </div>
  );
}

// ============ NEW CONTRACT MODAL ============
function NewContractModal({ onClose }) {
  const { addContract, brands, deals } = useAppState();
  const [contract, setContract] = useState({ title: '', brandId: '', dealId: '', value: '', template: 'custom', sections: [] });

  const handleTemplateSelect = (templateId) => {
    const template = CONTRACT_TEMPLATES.find(t => t.id === templateId);
    setContract({ ...contract, template: templateId, sections: template?.sections || [] });
  };

  const handleSubmit = () => {
    if (contract.title && contract.brandId) {
      addContract({ ...contract, value: Number(contract.value) || 0 });
      onClose();
    }
  };

  return (
    <Modal title="Create Contract" onClose={onClose} size="lg">
      <div className="p-6 space-y-5">
        <Input label="Contract Title" value={contract.title} onChange={(e) => setContract({ ...contract, title: e.target.value })} placeholder="e.g., Q1 Sponsorship Agreement" />
        <div className="grid grid-cols-2 gap-4">
          <Select label="Brand" value={contract.brandId} onChange={(e) => setContract({ ...contract, brandId: e.target.value })} options={brands.map(b => ({ value: b.id, label: b.name }))} placeholder="Select brand..." />
          <Input label="Value ($)" type="number" value={contract.value} onChange={(e) => setContract({ ...contract, value: e.target.value })} placeholder="0" />
        </div>
        <div>
          <label className="block text-xs text-neutral-500 mb-2 font-medium">Template</label>
          <div className="grid grid-cols-2 gap-3">
            {CONTRACT_TEMPLATES.map(t => (
              <button key={t.id} onClick={() => handleTemplateSelect(t.id)} className={`p-4 rounded-xl border text-left transition-colors ${contract.template === t.id ? 'border-lime-400 bg-lime-400/10' : 'border-neutral-700 hover:border-neutral-600'}`}>
                <p className="text-white font-medium mb-1">{t.name}</p>
                <p className="text-xs text-neutral-400">{t.description}</p>
              </button>
            ))}
          </div>
        </div>
      </div>
      <div className="border-t border-neutral-800 px-6 py-4 flex justify-end gap-3">
        <Button variant="secondary" onClick={onClose}>Cancel</Button>
        <Button onClick={handleSubmit} disabled={!contract.title || !contract.brandId}>Create Contract</Button>
      </div>
    </Modal>
  );
}

// ============ MARKETPLACE VIEW ============
function MarketplaceView() {
  const { opportunities } = useAppState();
  const [showNew, setShowNew] = useState(false);

  return (
    <div className="flex-1 flex flex-col overflow-hidden bg-neutral-950">
      <header className="px-4 lg:px-6 py-4 lg:py-5 border-b border-neutral-800/50">
        <div className="flex items-center justify-between gap-3">
          <div className="min-w-0">
            <h1 className="text-xl lg:text-2xl font-bold text-white">Marketplace</h1>
            <p className="text-xs lg:text-sm text-neutral-500 mt-0.5 hidden sm:block">Browse and apply to brand opportunities</p>
          </div>
          <Button onClick={() => setShowNew(true)} icon={Plus} className="shrink-0">
            <span className="hidden sm:inline">Post Opportunity</span>
            <span className="sm:hidden">Post</span>
          </Button>
        </div>
      </header>
      <main className="flex-1 overflow-auto p-4 lg:p-6">
        {opportunities.length === 0 ? (
          <EmptyState icon={Briefcase} title="No opportunities yet" description="Post or browse opportunities to connect with brands." action="Post Opportunity" onAction={() => setShowNew(true)} />
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 lg:gap-4">
            {opportunities.filter(o => o.status === 'open').map(opp => (
              <div key={opp.id} className="bg-neutral-900/50 border border-neutral-800/50 rounded-2xl p-5 hover:bg-neutral-900 transition-all">
                <div className="flex items-start gap-4 mb-4">
                  <BrandLogo domain={opp.brandDomain} name={opp.brandName} size={52} />
                  <div className="flex-1 min-w-0">
                    <h3 className="text-white font-semibold">{opp.title}</h3>
                    <p className="text-sm text-neutral-500">{opp.brandName}</p>
                  </div>
                </div>
                <p className="text-sm text-neutral-400 mb-4 line-clamp-2">{opp.description}</p>
                <div className="flex items-center justify-between">
                  <span className="text-emerald-400 font-semibold">{opp.budgetRange}</span>
                  <span className="text-xs text-neutral-500 flex items-center gap-1"><Users size={14} />{opp.applicantCount || 0}</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}

// ============ INVITES VIEW ============
function InvitesView() {
  const { getMyInvites, respondToInvite, addDeal, showToast } = useAppState();
  const invites = getMyInvites();
  const pending = invites.filter(i => i.status === 'pending');

  const handleAccept = (invite) => {
    addDeal({ brandId: invite.brandId, brandName: invite.brandName, title: invite.title, value: invite.value || 0, stage: 'negotiating', platform: invite.platform });
    respondToInvite(invite.id, 'accepted');
  };

  return (
    <div className="flex-1 flex flex-col overflow-hidden bg-neutral-950">
      <header className="px-4 lg:px-6 py-4 lg:py-5 border-b border-neutral-800/50">
        <h1 className="text-xl lg:text-2xl font-bold text-white">Private Invites</h1>
        <p className="text-xs lg:text-sm text-neutral-500 mt-0.5">Direct deal invitations from brands</p>
      </header>
      <main className="flex-1 overflow-auto p-4 lg:p-6">
        {invites.length === 0 ? (
          <EmptyState icon={Mail} title="No invites yet" description="When brands send you private deal invitations, they'll appear here." />
        ) : (
          <div className="space-y-4">
            {pending.map(invite => (
              <div key={invite.id} className="bg-neutral-900/50 border border-neutral-800/50 rounded-2xl p-5">
                <div className="flex items-start gap-4 mb-4">
                  <BrandLogo domain={invite.brandDomain} name={invite.brandName} size={56} />
                  <div className="flex-1">
                    <h3 className="text-white font-semibold text-lg">{invite.title}</h3>
                    <p className="text-neutral-400">{invite.brandName}</p>
                    <div className="flex items-center gap-3 mt-2">
                      <span className="text-emerald-400 font-bold text-lg">{formatCurrency(invite.value)}</span>
                      <span className="text-neutral-500">·</span>
                      <span className="text-neutral-400">{invite.platform}</span>
                    </div>
                  </div>
                </div>
                <div className="flex gap-3">
                  <Button onClick={() => handleAccept(invite)} className="flex-1">Accept</Button>
                  <Button variant="secondary" onClick={() => { respondToInvite(invite.id, 'declined'); showToast('Invite declined'); }} className="flex-1">Decline</Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}

// ============ MEDIA KIT VIEW ============
function MediaKitView() {
  const { mediaKit, setMediaKit, showToast } = useAppState();
  const [isEditing, setIsEditing] = useState(false);
  const [edited, setEdited] = useState(mediaKit);

  const handleSave = () => {
    setMediaKit(edited);
    setIsEditing(false);
    showToast('Media kit saved!');
  };

  return (
    <div className="flex-1 flex flex-col overflow-hidden bg-neutral-950">
      <header className="px-4 lg:px-6 py-4 lg:py-5 border-b border-neutral-800/50">
        <div className="flex items-center justify-between gap-3">
          <div className="min-w-0">
            <h1 className="text-xl lg:text-2xl font-bold text-white">Media Kit</h1>
            <p className="text-xs lg:text-sm text-neutral-500 mt-0.5 hidden sm:block">Showcase your creator profile to brands</p>
          </div>
          {isEditing ? (
            <div className="flex gap-2 shrink-0">
              <Button variant="secondary" onClick={() => { setEdited(mediaKit); setIsEditing(false); }} className="hidden sm:flex">Cancel</Button>
              <Button onClick={handleSave} icon={Save}>Save</Button>
            </div>
          ) : (
            <Button onClick={() => setIsEditing(true)} icon={Edit2} className="shrink-0">Edit</Button>
          )}
        </div>
      </header>
      <main className="flex-1 overflow-auto p-4 lg:p-6">
        <div className="max-w-3xl mx-auto space-y-6">
          <div className="bg-neutral-900/50 border border-neutral-800/50 rounded-2xl p-6">
            <h2 className="text-lg font-semibold text-white mb-4">Profile Information</h2>
            <div className="grid grid-cols-2 gap-4">
              {isEditing ? (
                <>
                  <Input label="Display Name" value={edited.displayName} onChange={(e) => setEdited({ ...edited, displayName: e.target.value })} />
                  <Input label="Niche" value={edited.niche} onChange={(e) => setEdited({ ...edited, niche: e.target.value })} />
                  <Input label="Location" value={edited.location} onChange={(e) => setEdited({ ...edited, location: e.target.value })} />
                  <Input label="Email" value={edited.email} onChange={(e) => setEdited({ ...edited, email: e.target.value })} />
                  <Textarea label="Bio" value={edited.bio} onChange={(e) => setEdited({ ...edited, bio: e.target.value })} className="col-span-2" rows={3} />
                </>
              ) : (
                <>
                  <div><p className="text-xs text-neutral-500 mb-1">Display Name</p><p className="text-white">{mediaKit.displayName || '—'}</p></div>
                  <div><p className="text-xs text-neutral-500 mb-1">Niche</p><p className="text-white">{mediaKit.niche || '—'}</p></div>
                  <div><p className="text-xs text-neutral-500 mb-1">Location</p><p className="text-white">{mediaKit.location || '—'}</p></div>
                  <div><p className="text-xs text-neutral-500 mb-1">Email</p><p className="text-white">{mediaKit.email || '—'}</p></div>
                  <div className="col-span-2"><p className="text-xs text-neutral-500 mb-1">Bio</p><p className="text-white whitespace-pre-wrap">{mediaKit.bio || '—'}</p></div>
                </>
              )}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}

// ============ ANALYTICS VIEW ============
function AnalyticsView() {
  const { deals, brands, getStats } = useAppState();
  const stats = getStats();

  const dealsByMonth = useMemo(() => {
    const months = {};
    deals.forEach(d => {
      const month = new Date(d.createdAt).toLocaleString('default', { month: 'short', year: 'numeric' });
      if (!months[month]) months[month] = { count: 0, value: 0 };
      months[month].count++;
      months[month].value += d.value || 0;
    });
    return months;
  }, [deals]);

  return (
    <div className="flex-1 flex flex-col overflow-hidden bg-neutral-950">
      <header className="px-4 lg:px-6 py-4 lg:py-5 border-b border-neutral-800/50">
        <h1 className="text-xl lg:text-2xl font-bold text-white">Analytics</h1>
        <p className="text-xs lg:text-sm text-neutral-500 mt-0.5">Track your performance and earnings</p>
      </header>
      <main className="flex-1 overflow-auto p-4 lg:p-6">
        <Stats />
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-6 mt-4 lg:mt-6">
          <div className="bg-neutral-900/50 border border-neutral-800/50 rounded-2xl p-6">
            <h3 className="text-lg font-semibold text-white mb-4">Deals by Stage</h3>
            <div className="space-y-3">
              {STAGES.map(stage => {
                const stageDeals = deals.filter(d => d.stage === stage.id);
                const percentage = deals.length > 0 ? (stageDeals.length / deals.length) * 100 : 0;
                return (
                  <div key={stage.id}>
                    <div className="flex justify-between text-sm mb-1">
                      <span className="text-neutral-400">{stage.name}</span>
                      <span className="text-white">{stageDeals.length}</span>
                    </div>
                    <div className="h-2 bg-neutral-800 rounded-full overflow-hidden">
                      <div className={`h-full ${stage.color}`} style={{ width: `${percentage}%` }} />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
          <div className="bg-neutral-900/50 border border-neutral-800/50 rounded-2xl p-6">
            <h3 className="text-lg font-semibold text-white mb-4">Top Brands</h3>
            <div className="space-y-3">
              {brands.slice(0, 5).map(brand => {
                const brandDeals = deals.filter(d => d.brandId === brand.id);
                const totalValue = brandDeals.reduce((sum, d) => sum + (d.value || 0), 0);
                return (
                  <div key={brand.id} className="flex items-center gap-3">
                    <BrandLogo domain={brand.domain} name={brand.name} size={36} />
                    <div className="flex-1">
                      <p className="text-white font-medium">{brand.name}</p>
                      <p className="text-xs text-neutral-500">{brandDeals.length} deals</p>
                    </div>
                    <span className="text-emerald-400 font-bold">{formatCurrency(totalValue)}</span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}

// ============ SETTINGS VIEW ============
function SettingsView() {
  const { profile, setProfile, showToast, deals } = useAppState();
  const { user, logout, tier, subscription } = useAuth();
  const [edited, setEdited] = useState(profile);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [isUpgrading, setIsUpgrading] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);

  // Stripe Price IDs
  const STRIPE_PRICES = {
    starter: 'price_1ShwceRpAhLDkbDGfTqAMHlJ',
    pro: 'price_1ShwctRpAhLDkbDGWI1TI0tP',
    business: 'price_1Shwd7RpAhLDkbDGcxUYpsGJ',
    enterprise: 'price_1ShwdYRpAhLDkbDG09cqFPM3',
  };

  // Pricing aligned with Supabase tier_configs table
  const PLAN_DETAILS = {
    free: { name: 'Free', price: 0, deals: 3, features: ['3 active deals', 'Basic pipeline', 'Invoice tracking'] },
    starter: { name: 'Starter', price: 15, deals: 10, features: ['10 active deals', 'E-signatures', 'Media kit builder'] },
    pro: { name: 'Pro', price: 40, deals: 30, features: ['30 active deals', 'Advanced analytics', 'Priority support'] },
    business: { name: 'Business', price: 100, deals: 100, features: ['100 active deals', 'Team collaboration', 'API access'] },
    enterprise: { name: 'Enterprise', price: 200, deals: -1, features: ['Unlimited deals', 'Custom branding', 'Dedicated support'] },
  };

  const handleSave = () => {
    setProfile(edited);
    showToast('Settings saved!');
  };

  const handleLogout = () => {
    setIsLoggingOut(true);
    localStorage.clear();
    sessionStorage.clear();
    logout().catch(console.error);
    setTimeout(() => {
      window.location.href = '/';
    }, 100);
  };

  const handleUpgrade = async (planId) => {
    if (!STRIPE_PRICES[planId]) {
      showToast('Invalid plan selected', 'error');
      return;
    }

    setIsUpgrading(true);
    try {
      const response = await fetch('/api/create-checkout-session', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          priceId: STRIPE_PRICES[planId],
          userEmail: user?.email,
          userId: user?.id,
          successUrl: `${window.location.origin}/?success=true`,
          cancelUrl: `${window.location.origin}/?canceled=true`,
        }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`API error: ${response.status}`);
      }

      const data = await response.json();
      
      if (data.error) {
        throw new Error(data.error);
      }

      // Redirect to Stripe Checkout using the URL (new method)
      if (data.url) {
        window.location.href = data.url;
      } else {
        throw new Error('No checkout URL returned');
      }
    } catch (error) {
      console.error('Upgrade error:', error);
      showToast(`Checkout failed: ${error.message}`, 'error');
    } finally {
      setIsUpgrading(false);
    }
  };

  // Direct Stripe Customer Portal link
  const STRIPE_BILLING_PORTAL = 'https://billing.stripe.com/p/login/eVq00l8bv0pvcevfr9gw000';

  const handleManageSubscription = () => {
    // Use direct billing portal link
    window.open(STRIPE_BILLING_PORTAL, '_blank');
  };

  const tierInfo = PLAN_DETAILS[tier] || PLAN_DETAILS.free;
  const activeDealsCount = deals.filter(d => !['paid'].includes(d.stage)).length;
  const dealLimitReached = tierInfo.deals !== -1 && activeDealsCount >= tierInfo.deals;

  return (
    <div className="flex-1 flex flex-col overflow-hidden bg-neutral-950">
      <header className="px-4 lg:px-6 py-4 lg:py-5 border-b border-neutral-800/50">
        <h1 className="text-xl lg:text-2xl font-bold text-white">Settings</h1>
        <p className="text-xs lg:text-sm text-neutral-500 mt-0.5">Manage your account and preferences</p>
      </header>
      <main className="flex-1 overflow-auto p-4 lg:p-6">
        <div className="max-w-2xl space-y-4 lg:space-y-6">
          {/* Subscription */}
          <div className="bg-neutral-900/50 border border-neutral-800/50 rounded-2xl p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-white">Current Plan</h2>
              <Badge color="lime">{tierInfo.name}</Badge>
            </div>
            
            {/* Usage */}
            <div className="bg-neutral-800/50 rounded-xl p-4 mb-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-neutral-400">Active Deals</span>
                <span className="text-sm text-white font-medium">
                  {activeDealsCount} / {tierInfo.deals === -1 ? '∞' : tierInfo.deals}
                </span>
              </div>
              {tierInfo.deals !== -1 && (
                <div className="h-2 bg-neutral-700 rounded-full overflow-hidden">
                  <div
                    className={`h-full rounded-full transition-all ${dealLimitReached ? 'bg-red-500' : 'bg-lime-400'}`}
                    style={{ width: `${Math.min((activeDealsCount / tierInfo.deals) * 100, 100)}%` }}
                  />
                </div>
              )}
              {dealLimitReached && (
                <p className="text-xs text-red-400 mt-2 flex items-center gap-1">
                  <AlertCircle size={12} />
                  Deal limit reached. Upgrade to add more deals.
                </p>
              )}
            </div>

            <div className="flex gap-3">
              {tier !== 'free' && (
                <Button icon={CreditCard} variant="secondary" onClick={handleManageSubscription}>
                  Manage Billing
                </Button>
              )}
              <Button onClick={() => setShowUpgradeModal(true)} className="flex-1">
                {tier === 'free' ? 'Upgrade Plan' : 'Change Plan'}
              </Button>
            </div>
          </div>

          {/* Profile */}
          <div className="bg-neutral-900/50 border border-neutral-800/50 rounded-2xl p-6">
            <h2 className="text-lg font-semibold text-white mb-4">Profile</h2>
            <div className="space-y-4">
              <Input label="Name" value={edited.name || ''} onChange={(e) => setEdited({ ...edited, name: e.target.value })} />
              <Input label="Email" value={user?.email || edited.email || ''} disabled />
              <Button onClick={handleSave}>Save Changes</Button>
            </div>
          </div>

          {/* Account Actions */}
          <div className="bg-neutral-900/50 border border-neutral-800/50 rounded-2xl p-6">
            <h2 className="text-lg font-semibold text-white mb-4">Account</h2>
            <Button
              variant="danger"
              icon={LogOut}
              onClick={handleLogout}
              disabled={isLoggingOut}
            >
              {isLoggingOut ? 'Signing out...' : 'Sign Out'}
            </Button>
          </div>
        </div>
      </main>

      {/* Upgrade Modal */}
      {showUpgradeModal && (
        <Modal title="Choose Your Plan" onClose={() => setShowUpgradeModal(false)} size="xl">
          <div className="p-4 lg:p-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 lg:gap-4">
              {Object.entries(PLAN_DETAILS).filter(([id]) => id !== 'free').map(([id, plan]) => (
                <div
                  key={id}
                  className={`rounded-2xl border p-5 transition-all flex flex-col ${
                    tier === id
                      ? 'border-lime-400 bg-lime-400/10'
                      : 'border-neutral-700 hover:border-neutral-600'
                  }`}
                >
                  <h3 className="text-lg font-bold text-white mb-1">{plan.name}</h3>
                  <div className="flex items-baseline gap-1 mb-4">
                    <span className="text-3xl font-bold text-white">${plan.price}</span>
                    <span className="text-neutral-500">/mo</span>
                  </div>
                  <ul className="space-y-2 flex-1">
                    {plan.features.map((feature, i) => (
                      <li key={i} className="text-sm text-neutral-400 flex items-start gap-2">
                        <Check size={16} className="text-lime-400 shrink-0 mt-0.5" />
                        {feature}
                      </li>
                    ))}
                  </ul>
                  <Button
                    onClick={() => handleUpgrade(id)}
                    disabled={tier === id || isUpgrading}
                    variant={tier === id ? 'secondary' : 'primary'}
                    className="w-full mt-6"
                  >
                    {tier === id ? 'Current Plan' : isUpgrading ? 'Processing...' : 'Upgrade'}
                  </Button>
                </div>
              ))}
            </div>
            <p className="text-xs text-neutral-500 text-center mt-6">
              All plans include a 14-day money-back guarantee. Cancel anytime.
            </p>
          </div>
        </Modal>
      )}
    </div>
  );
}

// ============ MOBILE HEADER ============
function MobileHeader({ onMenuClick }) {
  return (
    <header className="lg:hidden fixed top-0 left-0 right-0 z-30 bg-neutral-950 border-b border-neutral-800/50 px-4 py-3">
      <div className="flex items-center justify-between">
        <button onClick={onMenuClick} className="p-2 text-neutral-400 hover:text-white">
          <LayoutDashboard size={24} />
        </button>
        <div className="flex items-center gap-2">
          <SponsyLogo size={28} />
          <span className="text-lg font-bold text-white">Sponsy</span>
        </div>
        <div className="w-10" /> {/* Spacer for balance */}
      </div>
    </header>
  );
}

// ============ MAIN APP ROUTER ============
function MainApp() {
  const { currentView, isLoading, toast } = useAppState();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  if (isLoading) {
    return <LoadingScreen />;
  }

  const renderView = () => {
    switch (currentView) {
      case 'pipeline': return <PipelineView />;
      case 'brands': return <BrandsView />;
      case 'invoices': return <InvoicesView />;
      case 'contracts': return <ContractsView />;
      case 'marketplace': return <MarketplaceView />;
      case 'invites': return <InvitesView />;
      case 'mediakit': return <MediaKitView />;
      case 'analytics': return <AnalyticsView />;
      case 'settings': return <SettingsView />;
      default: return <PipelineView />;
    }
  };

  return (
    <div className="flex h-screen bg-neutral-950 text-white overflow-hidden">
      {/* Mobile header */}
      <MobileHeader onMenuClick={() => setSidebarOpen(true)} />
      
      {/* Sidebar */}
      <Sidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />
      
      {/* Main content - add top padding on mobile for header */}
      <div className="flex-1 flex flex-col pt-14 lg:pt-0 overflow-hidden">
        {renderView()}
      </div>
      
      <Toast toast={toast} />
    </div>
  );
}

// ============ ROOT APP COMPONENT ============
export default function App() {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return <LoadingScreen />;
  }

  if (!isAuthenticated) {
    return <AuthPage />;
  }

  return (
    <AppStateProvider>
      <MainApp />
    </AppStateProvider>
  );
}
